// src/components/AddCardScreen.tsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js';
import type { StripeCardNumberElementChangeEvent } from '@stripe/stripe-js';
import { FaArrowLeft } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../store/store';
import { addCard, resetAddCardState } from '../../store/reducers/addCardReducer';
import './AddCardScreen.css';

// const stripePromise = loadStripe(
//   'pk_test_51PX66aCTXHQWQf5MIvqOA1jYIwQwCQnKilmGggQi82B31irmsVLzaPzEFmvXp5sKVFKQjC9mAXzCQ9Gx4mZmmMV6000rbBQav6'
// );
const stripePromise = loadStripe(
  'pk_live_51PX66aCTXHQWQf5M7rEymc9OKIMthsotT3xaMCF91VzjnSJjhAR4T7krET5YDipGutfCLXu3i6ptSaXKZVydnBcn00TIDF42nT'
);

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      iconColor: '#555',
      color: '#333',
      fontSize: '16px',
      '::placeholder': { color: '#999' },
    },
    invalid: { color: '#fa755a' },
  },
};

const AddCardForm: React.FC = () => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { isLoading, error, success } = useSelector(
    (state: RootState) => state.addCard
  );

  const [fullName, setFullName] = useState('');
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [cardBrand, setCardBrand] = useState<string>('');

  useEffect(() => {
    if (success) {
      dispatch(resetAddCardState());
      navigate(-1);
    }
  }, [success, dispatch, navigate]);

  const handleCardNumberChange = (
    event: StripeCardNumberElementChangeEvent
  ) => {
    setCardBrand(event.brand);
    if (event.error) {
      setStripeError(event.error.message);
    } else {
      setStripeError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStripeError(null);

    if (!stripe || !elements) {
      setStripeError('Stripe.js not loaded yet.');
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);
    if (!cardElement) {
      setStripeError('Card number field not found.');
      return;
    }

    const { token, error: stripeErr } = await stripe.createToken(cardElement, {
      name: fullName.trim(),
    });
    if (stripeErr || !token) {
      setStripeError(stripeErr?.message ?? 'Failed to create card token');
      return;
    }

    const last4 = token.card?.last4 ?? '';
    const brand = token.card?.brand ?? 'other';

    dispatch(
      addCard({
        token: token.id,
        last4,
        brand,
        isDefault: true,
      })
    );
  };

  return (
    <div className="add-card-container">
<div className="add-card-header">
  <button className="back-button" onClick={() => navigate(-1)}>
    <FaArrowLeft />
  </button>
  <h1 className="add-card-title">Add New Card</h1>
</div>


      <form className="card-form" onSubmit={handleSubmit}>
        <label>
          Full Name
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Cardholder Name"
            required
          />
        </label>

        <label>
          Card Number
          <div className="stripe-input">
            <CardNumberElement
              options={CARD_ELEMENT_OPTIONS}
              onChange={handleCardNumberChange}
            />
            {cardBrand && (
              <span className="card-brand">{cardBrand.toUpperCase()}</span>
            )}
          </div>
        </label>

        <div className="small-inputs">
          <label>
            Expiry Date
            <div className="stripe-input">
              <CardExpiryElement options={CARD_ELEMENT_OPTIONS} />
            </div>
          </label>

          <label>
            CVC
            <div className="stripe-input">
              <CardCvcElement options={CARD_ELEMENT_OPTIONS} />
            </div>
          </label>
        </div>

        {(stripeError || error) && (
          <div className="error-text">{stripeError || error}</div>
        )}

        <div className="form-actions">
          <button
            type="submit"
            className="submit-button"
            disabled={!stripe || isLoading}
          >
            {isLoading ? 'Processing…' : 'Add Card'}
          </button>
        </div>
      </form>
    </div>
  );
};

const AddCardScreen: React.FC = () => (
  <Elements stripe={stripePromise}>
    <AddCardForm />
  </Elements>
);

export default AddCardScreen;
